import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { CalendarTwoIcon, LocationOneIcon, TelephoneIcon, UserTwoIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import { useQuery } from '@tanstack/react-query'

import { getEnrollment } from '../../../../api/enrollments/get-enrollment'
import { useNativeCountryName } from '../../../../lib/hooks/use-native-country-name'
import { enrollmentFrenchLeaderFields } from '../../../../lib/types/fields/enrollments/enrollment'
import { CenterLight } from '../../../../lib/types/models/center/center'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import OverviewCard from '../overview-card'

type Props = {
  enrollmentId: number
  center?: Pick<CenterLight, 'mobileNumberForFrenchLeader'> & {
    diffusedCenterManager?: {
      staff: Pick<
        NonNullable<CenterLight['diffusedCenterManager']>['staff'],
        'firstName' | 'lastName' | 'phoneNumber'
      >
    }
  }
}

const ClientFrenchManagerCard = ({ enrollmentId, center }: Props) => {
  const nativeCountryName = useNativeCountryName()
  const { data } = useQuery({
    queryKey: ['enrollment-french-leader-info', enrollmentId, enrollmentFrenchLeaderFields],
    queryFn: async () => getEnrollment(enrollmentId, enrollmentFrenchLeaderFields)
  })

  if (!data) return <Loader center />
  // the card describes a center stay with a French leader, so it only applies to journeys
  if (!data.enrollmentCenter || !data.mainProposition) return null
  const { hostFamily } = data.enrollmentCenter
  const { departureDate, returnDate } = data.mainProposition.journey.date
  return (
    <OverviewCard
      title="Coordonnées de son responsable français"
      subtitle={`Hébergé chez la famille-hotesse - ${hostFamily ? hostFamily.familyName : 'Non renseigné'}`}
      bulletPoints={[
        {
          icon: UserTwoIcon,
          label: `Responsable du séjour : ${valueRender(
            center?.diffusedCenterManager?.staff,
            (m) =>
              `${toFullName(m.firstName, m.lastName, { options: { mode: 'lastname-firstname' } })}`
          )} `
        },
        {
          icon: CalendarTwoIcon,
          label: `Du ${DateFormatter.DDDMYYYY(departureDate)} au ${DateFormatter.DDDMYYYY(returnDate)}`
        },
        {
          icon: LocationOneIcon,
          label: hostFamily
            ? `${hostFamily.address1}${hostFamily.address2 ? ` ${hostFamily.address2}` : ''}, ${hostFamily.zipCode} ${hostFamily.city}, ${nativeCountryName(hostFamily.country)}`
            : 'Adresse non renseignée'
        },
        {
          icon: TelephoneIcon,
          label: `Téléphone: ${valueRender(center?.mobileNumberForFrenchLeader?.phoneNumber, formatPhoneNumber)}`
        }
      ]}
      imagePath="/group-people.png"
    />
  )
}

export default ClientFrenchManagerCard
